/**
 * Insight Section Content
 * Renders content items within an insight section based on type and status
 */

import { makeStyles, tokens, Text, Link, Spinner } from '@fluentui/react-components'
import type { InsightSectionStatus, InsightContentItem } from '../../../domain'
import { validateSafeUrl } from '../../../utils/urlUtils'
import {
  ActionButtonItem,
  ChecklistItem,
  ProgressStepItem,
  ReferenceLinkItem,
} from './contentItems'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  item: {
    lineHeight: tokens.lineHeightBase300,
  },
  bullet: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  bulletMarker: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  mention: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
})

export interface InsightSectionContentProps {
  status: InsightSectionStatus
  items: InsightContentItem[]
  errorMessage?: string
}

export function InsightSectionContent({ status, items, errorMessage }: InsightSectionContentProps) {
  const styles = useStyles()

  if (status === 'pending') {
    return (
      <div className={styles.loading}>
        <Spinner size="tiny" />
        <Text size={200}>Analyzing...</Text>
      </div>
    )
  }

  if (status === 'error') {
    return <Text className={styles.error}>{errorMessage ?? 'An error occurred'}</Text>
  }

  if (status === 'empty' || items.length === 0) {
    return <Text className={styles.empty}>No information available</Text>
  }

  return (
    <div className={styles.root}>
      {items.map((item) => (
        <ContentItem key={item.id} item={item} styles={styles} />
      ))}
    </div>
  )
}

interface ContentItemProps {
  item: InsightContentItem
  styles: ReturnType<typeof useStyles>
}

function ContentItem({ item, styles }: ContentItemProps) {
  switch (item.type) {
    case 'text':
      return <Text className={styles.item}>{item.content}</Text>

    case 'bullet':
      return (
        <div className={styles.bullet}>
          <span className={styles.bulletMarker}>•</span>
          <Text className={styles.item}>{item.content}</Text>
        </div>
      )

    case 'link': {
      const { isSafe, href } = validateSafeUrl(item.href)
      if (isSafe && item.href) {
        return (
          <Link href={href} target="_blank" rel="noopener noreferrer">
            {item.content}
          </Link>
        )
      }
      return <Text className={styles.item}>{item.content}</Text>
    }

    case 'mention':
      return (
        <Text className={styles.item}>
          {item.content} <span className={styles.mention}>@{item.mentionName}</span>
        </Text>
      )

    case 'actionButton':
      return <ActionButtonItem item={item} />

    case 'checklist':
      return <ChecklistItem item={item} />

    case 'progressStep':
      return <ProgressStepItem item={item} />

    case 'referenceLink':
      return <ReferenceLinkItem item={item} />

    default:
      return <Text className={styles.item}>{item.content}</Text>
  }
}
